/**
 * Shared hooks for admin and dashboard functionality
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

/**
 * Hook for handling API calls with loading states
 */
export function useApiCall() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (apiCall, successMessage, errorMessage) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiCall();
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (err) {
      setError(err.message);
      toast.error(errorMessage || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, execute };
}

/**
 * Hook for handling delete operations with confirmation
 */
export function useDeleteWithConfirm(onDelete, onSuccess) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = useCallback(async (id) => {
    setDeleting(true);
    try {
      await onDelete(id);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  }, [onDelete, onSuccess]);

  return { deleting, handleDelete };
}

/**
 * Get authorization headers
 */
export function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

/**
 * Format date for display
 */
export function formatDate(dateStr) {
  if (!dateStr) return 'No date';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format date with time
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return 'No date';
  try {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}
